/**
 * QuizResults Component
 * Display quiz completion results with score and performance message
 */

import { Button } from '../ui/Button';
import type { QuizResults as QuizResultsType } from '../../services/quiz.service';

export interface QuizResultsProps {
  results: QuizResultsType;
  onPlayAgain: () => void;
  onBackToHome: () => void;
}

export const QuizResults = ({ results, onPlayAgain, onBackToHome }: QuizResultsProps) => {
  const { correct_answers, incorrect_answers, score_percentage } = results;

  // Determine performance message and icon based on score
  const getPerformanceData = () => {
    if (score_percentage >= 90) {
      return {
        message: 'Outstanding! You really know your team!',
        icon: (
          <svg className="w-16 h-16 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ),
        bgClass: 'bg-status-warning-subtle',
        textClass: 'text-status-warning',
      };
    } else if (score_percentage >= 70) {
      return {
        message: 'Great job! You know most of your teammates!',
        icon: (
          <svg className="w-16 h-16 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        ),
        bgClass: 'bg-status-success-subtle',
        textClass: 'text-status-success',
      };
    } else if (score_percentage >= 50) {
      return {
        message: 'Good effort! Keep practicing to improve your score.',
        icon: (
          <svg className="w-16 h-16 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        ),
        bgClass: 'bg-status-info-subtle',
        textClass: 'text-status-info',
      };
    } else {
      return {
        message: 'Don\'t worry! Spend more time with your team and try again.',
        icon: (
          <svg className="w-16 h-16 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        bgClass: 'bg-orange-50 dark:bg-orange-900/20',
        textClass: 'text-orange-800 dark:text-orange-300',
      };
    }
  };

  const performance = getPerformanceData();

  return (
    <div className="bg-surface rounded-lg shadow p-8 text-center">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-heading mb-2">Quiz Complete!</h2>
        <p className="text-meta">Here's how you performed:</p>
      </div>

      {/* Score Display */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-status-success-subtle p-6 rounded-lg">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">{correct_answers}</div>
          <div className="text-sm text-status-success">Correct Answers</div>
        </div>
        <div className="bg-status-danger-subtle p-6 rounded-lg">
          <div className="text-3xl font-bold text-red-600 dark:text-red-400 mb-2">{incorrect_answers}</div>
          <div className="text-sm text-status-danger">Incorrect Answers</div>
        </div>
        <div className="bg-status-info-subtle p-6 rounded-lg">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">{score_percentage}%</div>
          <div className="text-sm text-status-info">Overall Score</div>
        </div>
      </div>

      {/* Performance Message */}
      <div className={`mb-8 p-4 rounded-lg ${performance.bgClass}`}>
        <p className={`font-medium ${performance.textClass}`}>{performance.message}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Button onClick={onPlayAgain}>
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Play Again
        </Button>
        <Button variant="secondary" onClick={onBackToHome}>
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          Back to Home
        </Button>
      </div>
    </div>
  );
};
